import { useState, useEffect } from 'react';
import { RefreshCw, IndianRupee, CreditCard, Calendar, TrendingUp, Download } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const formatAdminDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatAmount = (paise, currency = 'INR') => {
  const major = (paise || 0) / 100;
  return currency === 'USD'
    ? `$${major.toFixed(2)}`
    : `₹${major.toLocaleString('en-IN')}`;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function PaymentReport({ showSettlements = true } = {}) {
  const { toast } = useToast();
  const [report, setReport] = useState(null);
  const [settlements, setSettlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, settlementRes] = await Promise.all([
        adminSubscriptionsAPI.getPaymentReport({ period: 6 }),
        adminSubscriptionsAPI.getSettlements({ limit: 5 })
      ]);
      setReport(reportRes.data.data);
      setSettlements(settlementRes.data.data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await adminSubscriptionsAPI.syncSettlements();
      toast.success(data.data?.message || 'Settlements synced');
      const res = await adminSubscriptionsAPI.getSettlements({ limit: 5 });
      setSettlements(res.data.data);
    } catch (err) {
      toast.error(err.error || 'Failed to sync settlements');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-5 h-5 animate-spin text-surface-400" />
      </div>
    );
  }

  if (!report) return null;

  const { summary, byMonth, byMethod, byInterval, recentPayments } = report;
  const settlementSummary = settlements?.summary;

  return (
    <div className="space-y-4">
      {/* Revenue Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-green-50 rounded-xl p-3 border border-green-200">
          <span className="text-[10px] font-medium text-green-600 uppercase">Gross Revenue</span>
          <p className="text-lg font-bold text-green-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatAmount(summary.grossRevenue)}
          </p>
          <span className="text-[10px] text-green-600">{summary.paymentCount} payments</span>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
          <span className="text-[10px] font-medium text-blue-600 uppercase">Net Revenue</span>
          <p className="text-lg font-bold text-blue-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatAmount(summary.netRevenue)}
          </p>
          <span className="text-[10px] text-blue-600">after fees & tax</span>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
          <span className="text-[10px] font-medium text-amber-600 uppercase">Razorpay Fees</span>
          <p className="text-lg font-bold text-amber-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatAmount(summary.totalFees + summary.totalTax)}
          </p>
          <span className="text-[10px] text-amber-600">fees + GST</span>
        </div>
        <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
          <span className="text-[10px] font-medium text-purple-600 uppercase">Discounts</span>
          <p className="text-lg font-bold text-purple-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatAmount(summary.totalDiscounts)}
          </p>
          {summary.refundTotal > 0 && <span className="text-[10px] text-red-600">Refunds: {formatAmount(summary.refundTotal)}</span>}
        </div>
      </div>

      {/* Monthly Trend */}
      {byMonth.length > 0 && (
        <div className="bg-white border border-surface-100 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-3">Monthly Revenue</h4>
          <div className="space-y-2">
            {byMonth.map((m) => {
              const maxGross = Math.max(...byMonth.map(x => x.gross));
              const pct = maxGross > 0 ? (m.gross / maxGross) * 100 : 0;
              return (
                <div key={`${m._id.year}-${m._id.month}`} className="flex items-center gap-3">
                  <span className="text-xs text-surface-500 w-16 flex-shrink-0">{MONTH_NAMES[m._id.month - 1]} {String(m._id.year).slice(2)}</span>
                  <div className="flex-1 bg-surface-100 rounded-full h-5 overflow-hidden">
                    <div className="bg-green-500 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-surface-700 w-20 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatAmount(m.gross)}
                  </span>
                  <span className="text-[10px] text-surface-400 w-8 text-right">{m.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Breakdowns Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* By Method */}
        {byMethod.length > 0 && (
          <div className="bg-white border border-surface-100 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-3">By Payment Method</h4>
            <div className="space-y-2">
              {byMethod.map((m) => (
                <div key={m.method} className="flex items-center justify-between py-1.5 border-b border-surface-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-3.5 h-3.5 text-surface-400" />
                    <span className="text-sm font-medium text-surface-700 capitalize">{m.method}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(m.total)}</span>
                    <span className="text-[10px] text-surface-400 ml-1.5">{m.count}x</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By Interval */}
        {byInterval.length > 0 && (
          <div className="bg-white border border-surface-100 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-3">By Plan Interval</h4>
            <div className="space-y-2">
              {byInterval.map((i) => (
                <div key={i.interval} className="flex items-center justify-between py-1.5 border-b border-surface-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-surface-400" />
                    <span className="text-sm font-medium text-surface-700 capitalize">{i.interval}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(i.total)}</span>
                    <span className="text-[10px] text-surface-400 ml-1.5">{i.count}x</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Settlement Summary */}
      {showSettlements && settlementSummary && settlementSummary.count > 0 && (
        <div className="bg-white border border-surface-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Razorpay Settlements</h4>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
            >
              {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              {syncing ? 'Syncing...' : 'Sync'}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-surface-50 rounded-lg p-2.5">
              <span className="text-[10px] text-surface-400 uppercase block">Settled</span>
              <span className="text-sm font-bold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(settlementSummary.totalSettled)}</span>
            </div>
            <div className="bg-surface-50 rounded-lg p-2.5">
              <span className="text-[10px] text-surface-400 uppercase block">Fees</span>
              <span className="text-sm font-bold text-amber-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(settlementSummary.totalFees)}</span>
            </div>
            <div className="bg-surface-50 rounded-lg p-2.5">
              <span className="text-[10px] text-surface-400 uppercase block">Tax</span>
              <span className="text-sm font-bold text-amber-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(settlementSummary.totalTax)}</span>
            </div>
            <div className="bg-surface-50 rounded-lg p-2.5">
              <span className="text-[10px] text-surface-400 uppercase block">Net</span>
              <span className="text-sm font-bold text-green-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(settlementSummary.netRevenue)}</span>
            </div>
          </div>
          {/* Recent Settlements */}
          {settlements.settlements?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {settlements.settlements.map((s) => (
                <div key={s.razorpaySettlementId} className="flex items-center justify-between text-xs py-1.5 border-b border-surface-50 last:border-0">
                  <div>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.status === 'processed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      {s.status}
                    </span>
                    {s.utr && <span className="text-surface-400 font-mono ml-2">{s.utr}</span>}
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.amount)}</span>
                    <span className="text-surface-400 ml-1.5">{formatAdminDate(s.razorpayCreatedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No Settlements Yet */}
      {showSettlements && (!settlementSummary || settlementSummary.count === 0) && (
        <div className="bg-white border border-surface-100 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Razorpay Settlements</h4>
              <p className="text-xs text-surface-400 mt-1">No settlement data yet. Click Sync to pull from Razorpay.</p>
            </div>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
            >
              {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              {syncing ? 'Syncing...' : 'Sync from Razorpay'}
            </button>
          </div>
        </div>
      )}

      {/* Recent Payments */}
      {recentPayments?.length > 0 && (
        <div className="bg-white border border-surface-100 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-3">Recent Payments</h4>
          <div className="space-y-1.5">
            {recentPayments.map((p) => (
              <div key={p._id} className="flex items-center justify-between py-2 border-b border-surface-50 last:border-0 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                    p.status === 'success' ? 'bg-green-50 text-green-700' :
                    p.status === 'failed' ? 'bg-red-50 text-red-700' :
                    p.status === 'refunded' ? 'bg-purple-50 text-purple-700' :
                    'bg-amber-50 text-amber-700'
                  }`}>
                    {p.status}
                  </span>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-surface-900 truncate block">{p.userName || 'Unknown'}</span>
                    <span className="text-[10px] text-surface-400 capitalize">{p.method || '-'} · {p.paymentType || '-'}{p.coupon ? ` · ${p.coupon}` : ''}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-sm font-semibold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatAmount(p.amount, p.currency)}
                  </span>
                  <div className="text-[10px] text-surface-400">{formatAdminDate(p.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
